import React, { useState, useEffect, useCallback } from 'react';
import { getAppLockService } from '../../services/AppLockService';

interface PinEntryProps {
  onSuccess: () => void;
  onForgotPin: () => void;
}

const PIN_LENGTH = 6;
const MAX_ATTEMPTS = 8;
const LOCKOUT_AFTER = 5;
const LOCKOUT_SECONDS = 30;

const PinEntry: React.FC<PinEntryProps> = ({ onSuccess, onForgotPin }) => {
  const [digits, setDigits] = useState<string[]>([]);
  const [error, setError] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockoutRemaining, setLockoutRemaining] = useState(0);
  const [verifying, setVerifying] = useState(false);

  // Lockout countdown
  useEffect(() => {
    if (lockoutRemaining <= 0) return;
    const timer = setInterval(() => {
      setLockoutRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [lockoutRemaining]);

  const handleDigit = useCallback(
    async (digit: string) => {
      if (lockoutRemaining > 0 || verifying) return;

      const next = [...digits, digit];
      setDigits(next);

      if (next.length < PIN_LENGTH) return;

      // Full PIN entered — verify
      setVerifying(true);
      const pin = next.join('');

      // AppLockService.ts's verifyPin() wraps its ENTIRE body in its own
      // try/catch and always resolves to a boolean (false on any internal
      // failure -- missing salt, crypto error, or a native-biometric read
      // failure) -- it provably never rejects, so a catch here would be
      // error handling for an impossible scenario (CLAUDE.md Simplicity
      // First). Source-verified 2026-09-15, closing the "Not
      // source-verified" caveat this finding carried in
      // scripts/missing-catch-baseline.json.
      // missing-catch exempt: verifyPin() catches internally and never rejects (see above)
      try {
        const valid = await getAppLockService().verifyPin(pin);

        if (valid) {
          setDigits([]);
          onSuccess();
        } else {
          const newAttempts = attempts + 1;
          setAttempts(newAttempts);
          setError(true);
          setDigits([]);

          // Brief red flash then clear error
          setTimeout(() => setError(false), 600);

          if (newAttempts >= LOCKOUT_AFTER && newAttempts < MAX_ATTEMPTS) {
            setLockoutRemaining(LOCKOUT_SECONDS);
          }
        }
      } finally {
        setVerifying(false);
      }
    },
    [digits, attempts, lockoutRemaining, verifying, onSuccess]
  );

  const handleDelete = useCallback(() => {
    if (lockoutRemaining > 0) return;
    setDigits((prev) => prev.slice(0, -1));
  }, [lockoutRemaining]);

  const isLockedOut = lockoutRemaining > 0;
  const showForgotPin = attempts >= MAX_ATTEMPTS;

  return (
    <div className="app-lock-pin-entry flex flex-col items-center gap-6 w-full max-w-xs px-4">
      {/* 6-dot display */}
      <div className="flex gap-3">
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <div
            key={i}
            className={`w-4 h-4 rounded-full border-2 transition-all duration-150 ${
              i < digits.length
                ? error
                  ? 'bg-red-400 border-red-400'
                  : 'bg-white border-white'
                : 'bg-transparent border-white/40'
            }`}
          />
        ))}
      </div>

      {/* Error / lockout message */}
      {isLockedOut && (
        <p className="text-red-400 text-sm text-center">
          Too many attempts. Try again in {lockoutRemaining}s
        </p>
      )}
      {error && !isLockedOut && (
        <p className="text-red-400 text-sm">Incorrect PIN</p>
      )}

      {/* Numeric keypad */}
      <div className="grid grid-cols-3 gap-3 w-full">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button
            key={d}
            onClick={() => handleDigit(d)}
            disabled={isLockedOut || verifying}
            className="flex items-center justify-center h-14 rounded-xl bg-white/10 text-white text-xl font-medium border border-white/10 hover:bg-white/20 active:bg-white/30 transition-colors disabled:opacity-40"
          >
            {d}
          </button>
        ))}
        {/* Bottom row: empty, 0, delete */}
        <div className="h-14" />
        <button
          onClick={() => handleDigit('0')}
          disabled={isLockedOut || verifying}
          className="flex items-center justify-center h-14 rounded-xl bg-white/10 text-white text-xl font-medium border border-white/10 hover:bg-white/20 active:bg-white/30 transition-colors disabled:opacity-40"
        >
          0
        </button>
        <button
          onClick={handleDelete}
          disabled={isLockedOut || digits.length === 0}
          className="flex items-center justify-center h-14 rounded-xl text-white/60 text-base hover:text-white hover:bg-white/10 transition-colors disabled:opacity-40"
          aria-label="Delete last digit"
        >
          &#x232B;
        </button>
      </div>

      {/* Forgot PIN */}
      {showForgotPin && (
        <button
          onClick={onForgotPin}
          className="text-white/50 text-sm underline mt-2"
        >
          Forgot PIN? Recover your account
        </button>
      )}
    </div>
  );
};

export default PinEntry;
